import { BetterSupportIcon, FeatureParityIcon, HonestPricingIcon, NoLockInIcon, TelemetryPatternIcon, ZeroConfigIcon } from "../icons-v2";
import type {
  ComparisonSection,
  ComparisonTableRow,
  SwitchStatsSection,
} from "./comparison-types";

export type {
  ComparisonColumnContent,
  ComparisonSection,
  ComparisonTableCell,
  ComparisonTableRow,
  PricingRow,
  SwitchStatItem,
  SwitchStatsSection,
} from "./comparison-types";

export const NEWRELIC_COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    id: "full-feature-parity",
    title: "Full Feature Parity",
    middleware: {
      description:
        "APM, infrastructure, logs, distributed tracing, RUM, synthetics, database monitoring, containers, serverless, browser testing, and LLM observability—all bundled in one platform with predictable pricing.",
      bullets: [
        "Every observability capability included in one plan",
        "No separate SKUs for APM, RUM, Log management",
        "LLM observability included at no extra cost",
        "One platform for every telemetry signal",
      ],
      image: "",
    },
    competitor: {
      description:
        "New Relic delivers comprehensive observability, but pricing is spread across multiple products, user licenses, and platform meters. As adoption grows, costs increase across several billing dimensions.",
      bullets: [
        "Multiple pricing meters for the same platform",
        "Per-user licensing across plans",
        "Compute Capacity Units add another billing layer",
        "Extended retention requires additional add-ons",
      ],
    },
    icon: FeatureParityIcon,
  },
  {
    id: "no-lock-in",
    title: "No Vendor Lock-In",
    middleware: {
      description:
        "Built on OpenTelemetry from day one. Your instrumentation stays portable, your telemetry remains open, and logs, metrics, traces, and events correlate automatically in one timeline.",
      bullets: [
        "100% OpenTelemetry-native architecture",
        "OTel instrumentation works without changes",
        "Cloud, BYOC, hybrid, and on-prem deployments",
        "No proprietary agents or query language required",
      ],
      image: "/images/comparison/no-lock-in-middleware.webp",
    },
    competitor: {
      description:
        "New Relic supports OpenTelemetry, but the platform still relies on proprietary agents, NRQL, and proprietary storage. Migrating often means rebuilding queries and dashboards.",
      bullets: [
        "OpenTelemetry data stored in a proprietary format",
        "NRQL required for advanced investigations",
        "Migration requires rebuilding dashboards",
        "Proprietary platform limits portability",
      ],
      image: "/images/comparison/no-vendor-newrelic.webp",
    },
    icon: NoLockInIcon,
  },
  {
    id: "better-support",
    title: "Better Support and Experience",
    middleware: {
      description:
        "Every customer receives the same high-touch support experience, regardless of plan size, with direct access to engineers when production issues occur.",
      bullets: [
        "One-hour critical issue SLA on every plan",
        "Dedicated Slack and Microsoft Teams support",
        "Real engineers respond directly",
        "No enterprise-only support tiers",
      ],
    },
    competitor: {
      description:
        "Support quality depends on your subscription tier. Faster response times and dedicated support are reserved for Enterprise customers.",
      bullets: [
        "One-hour SLA available only on Enterprise",
        "Pro plans receive longer response targets",
        "Standard plans rely primarily on ticket support",
        "Dedicated support requires premium plans",
      ],
    },
    icon: BetterSupportIcon,
  },
  {
    id: "predictable-pricing",
    title: "Predictable Pricing, No Per-User Tax",
    middleware: {
      description:
        "Simple usage-based pricing with no hidden meters. Pay only for the data you send while every user gets access to the full platform.",
      bullets: [
        "Pay only for data ingested",
        "No per-user or CCU charges",
        "Unlimited users included",
        "180-day retention included",
      ],
    },
    competitor: {
      description:
        "New Relic combines data ingest, user licensing, Compute Capacity Units, and retention upgrades into one pricing model, making costs harder to forecast.",
      bullets: [
        "Per-GB ingest plus per-user pricing",
        "Compute Capacity Units billed separately",
        "Data Plus required for longer retention",
        "Usage spikes can significantly increase costs",
      ],
    },
    icon: HonestPricingIcon,
  },
  {
    id: "zero-config",
    title: "Zero Config",
    middleware: {
      description:
        "Deploy with one script and start monitoring immediately. No proprietary query language or complex onboarding required before teams become productive.",
      bullets: [
        "One-minute installation",
        "Automatic service and dependency discovery",
        "Visual query experience for everyday workflows",
      ],
      image: "/images/comparison/zero-config-middleware.webp",
    },
    competitor: {
      description:
        "New Relic provides powerful capabilities, but many workflows depend on NRQL, agent configuration, and ongoing telemetry tuning.",
      bullets: [
        "Agent configuration and tagging take time",
        "Onboarding can require significant setup",
        "Cost optimization needs continuous tuning",
      ],
      image: "/images/comparison/zero-config-newrelic.webp",
    },
    icon: ZeroConfigIcon,
  },
  {
    id: "telemetry-patterns",
    title: "Telemetry Patterns",
    middleware: {
      description:
        "Every signal is automatically connected, making investigations faster without relying on custom tagging or proprietary workflows.",
      bullets: [
        "Service-level alerting built in",
        "Search across every stored span",
        "Native logs-to-traces correlation",
        "High-cardinality queries without additional complexity",
      ],
    },
    competitor: {
      description:
        "New Relic supports advanced telemetry, but several workflows depend on NRQL, consistent tagging, and platform-specific configuration.",
      bullets: [
        "Span search depends on retention settings",
        "Correlation relies on tagging consistency",
        "NRQL required for advanced querying",
        "Configuration overhead grows with scale",
      ],
    },
    icon: TelemetryPatternIcon,
  },
];

export const NEWRELIC_FEATURE_TABLE_ROWS: ComparisonTableRow[] = [
  {
    id: "bottom-line",
    label: "Bottom line",
    subtitle: "Who each tool is really built for",
    middleware: {
      text: "Cost-conscious, OpenTelemetry-native, cloud-native teams",
    },
    competitor: {
      text: "Large enterprises with complex monitoring requirements",
    },
  },
  {
    id: "sre-agent",
    label: "SRE Agent ",
    subtitle: "(detect → fix → PR)",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "auto-remediation",
    label: "Auto-remediation ",
    subtitle: "(code + K8s)",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "pr-generation",
    label: "PR generation",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "byoc-deployment",
    label: "BYOC deployment",
    middleware: {
      status: "check",
      description: "Cloud, BYOC & On-prem",
    },
    competitor: {
      status: "cross",
      description: "Primarily SaaS",
    },
  },
  {
    id: "value-for-money",
    label: "Value for money",
    middleware: {
      status: "check",
      description: "Pay only for the data that you send",
    },
    competitor: {
      status: "cross",
      description: "Users, ingest, CCUs, and add-ons",
    },
  },
  {
    id: "otel-architecture",
    label: "OpenTelemetry architecture",
    middleware: {
      description: "Built natively on OpenTelemetry",
    },
    competitor: {
      description: "Combination of proprietary agents & NRQL.",
    },
  },
  {
    id: "migration",
    label: "Migration",
    middleware: {
      description: "No re-instrumentation",
    },
    competitor: {
      description: "Platform-specific migration",
    },
  },
  {
    id: "query-language",
    label: "Query language",
    middleware: {
      headline: "Visual UI + PromQL + SQL-like logs",
      headlineStyle: "bold",
      description: "No proprietary syntax required",
    },
    competitor: {
      headline: "NRQL",
      headlineStyle: "bold",
      description: "Advanced investigations rely on a proprietary language",
    },
  },
  {
    id: "per-user-pricing",
    label: "Per-user pricing",
    middleware: {
      headline: "None",
      headlineStyle: "gradient",
      description: "Unlimited users on every plan",
    },
    competitor: {
      headline: "Per-user licensing",
      headlineStyle: "bold",
      description: "User count impacts total cost",
    },
  },
  {
    id: "ingestion-control",
    label: "Ingestion control",
    middleware: {
      headline: "Built In",
      headlineStyle: "bold",
      description: "Drop noisy telemetry before storage",
    },
    competitor: {
      headline: "Basic filtering",
      headlineStyle: "bold",
      description: "Cost optimization depends on usage management",
    },
  },
  {
    id: "unified-investigations",
    label: "Unified investigations",
    middleware: {
      headline: "One timeline",
      headlineStyle: "bold",
      description: "Logs, metrics, traces, and events automatically correlated",
    },
    competitor: {
      headline: "Cross-product experience",
      headlineStyle: "bold",
      description: "Correlation depends on New Relic services.",
    },
  },
  {
    id: "logs-to-traces",
    label: "Logs-to-traces correlation",
    middleware: {
      headline: "Native",
      headlineStyle: "bold",
      description: "Jump directly from logs to related traces",
    },
    competitor: {
      headline: "Supported",
      headlineStyle: "bold",
      description: "Available with proper instrumentation",
    },
  },
  {
    id: "search-across-telemetry",
    label: "Search across telemetry",
    middleware: {
      headline: "Search every stored Span",
      headlineStyle: "bold",
      description: "No platform-specific limitations",
    },
    competitor: {
      headline: "Retention dependent",
      headlineStyle: "bold",
      description: "Search varies by plan and stored data",
    },
  },
  {
    id: "ai-operations",
    label: "AI operations",
    middleware: {
      headline: "OpsAI included",
      headlineStyle: "bold",
      description: "Investigates, remediates, and creates pull requests",
    },
    competitor: {
      headline: "AI assistance",
      headlineStyle: "bold",
      description: "Helps investigate and summarize incidents",
    },
  },
  {
    id: "auto-remediation-detail",
    label: "Auto-remediation",
    middleware: {
      headline: "Built in",
      headlineStyle: "bold",
      description: "Code fixes and Kubernetes remediation",
    },
    competitor: {
      headline: "Not available",
      headlineStyle: "bold",
      description: "Remediation remains manual",
    },
  },
  {
    id: "llm-observability",
    label: "LLM observability",
    middleware: {
      headline: "Included",
      headlineStyle: "bold",
      description: "No separate pricing",
    },
    competitor: {
      headline: "Available",
      headlineStyle: "bold",
      description: "Included within AI monitoring capabilities",
    },
  },
  {
    id: "data-retention",
    label: "Data retention",
    middleware: {
      headline: "180 days included",
      headlineStyle: "bold",
      description: "No additional retention charges",
    },
    competitor: {
      headline: "Plan dependent",
      headlineStyle: "bold",
      description: "Longer retention requires upgrades",
    },
  },
  {
    id: "apm-infra-logs-rum",
    label: "APM, Infrastructure, Logs & RUM",
    middleware: {
      description: "One platform, one pricing model",
    },
    competitor: {
      headline: "Broad coverage",
      headlineStyle: "bold",
      description: "Available across multiple platform capabilities",
    },
  },
  {
    id: "dedicated-support",
    label: "Dedicated technical support",
    middleware: {
      headline: "On all plans",
      headlineStyle: "bold",
      description: "Real support, even on the entry tier",
    },
    competitor: {
      headline: "Enterprise plans",
      headlineStyle: "bold",
      description: "Premium SLAs and dedicated support reserved for higher tiers",
    },
  },
];

export const NEWRELIC_SWITCH_STATS: SwitchStatsSection = {
  tag: "What customers get",
  title: "The numbers after the switch",
  description:
    "Averages across customers who migrated from New Relic to Middleware.",
  stats: [
    {
      id: "troubleshooting-speed",
      value: "4",
      unit: "×",
      title: "Troubleshooting",
      description:
        "Faster mean-time-to-detect with unified signals on one timeline.",
    },
    {
      id: "cost-reduction",
      value: "75",
      unit: "%",
      title: "Cost Reduction",
      description:
        "Predictable, usage-based billing replaces high-watermark host fees and the custom-metric tax",
    },
    {
      id: "support-response",
      value: "1",
      unit: "hr",
      title: "Issue response SLA",
      description: "Critical-issue response SLA on every plan",
    },
    {
      id: "opsai-automation",
      value: "80",
      unit: "%",
      title: "Auto fixes with accuracy",
      description:
        "OpsAI resolves issues end-to-end, saving 60% of engineer time.",
    },
  ],
};
